import { numberFormatter } from './formatters';

/**
 * Gets the decimal separator for a given locale by formatting a test number.
 * @param locales - The locale(s) to use. If not provided, uses the browser's locale.
 * @returns The decimal separator character (e.g., '.' for en-US, ',' for de-DE)
 * @example
 * getDecimalSeparator('de-DE') // returns ','
 * getDecimalSeparator('en-US') // returns '.'
 * getDecimalSeparator() // returns the separator for the browser's locale
 */
export function getDecimalSeparator(locales?: string | string[]): string {
  // Resolve to browser locale if not provided
  const resolvedLocales = locales ?? navigator.language;

  // Format 1.1 with exactly 1 decimal place
  const formatted = new Intl.NumberFormat(resolvedLocales, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(1.1);

  // The separator is always at index 1 for "1.1" or "1,1"
  return formatted.charAt(1);
}

/**
 * Sanitises a raw user-typed numeric string for a given locale.
 *
 * - Strips any character that is not a digit, dot, comma, or minus sign.
 * - Replaces the separator not used by the locale with the correct one.
 * - Preserves only the first occurrence of the decimal separator.
 * - Truncates digits after the decimal separator to `maxFractionDigits` when provided.
 *
 * The returned string is still in locale format (e.g. `'1,23'` for de-DE) and
 * is intended to be shown directly in the input element or fed to {@link decimalParser}.
 *
 * @param value - Raw string from the input element.
 * @param locale - BCP 47 locale tag (e.g. `'de-DE'`, `'en-US'`).
 * @param maxFractionDigits - Maximum digits after the decimal separator. `null` / `undefined` means no limit.
 * @returns The sanitised string in locale decimal format.
 * @example
 * sanitizeDecimalInput('1.234,567', 'de-DE', 2) // returns '1.234,56'
 * sanitizeDecimalInput('1,234.567', 'en-US', 2) // returns '1,234.56'
 * sanitizeDecimalInput('1,,2.3',    'en-US')    // returns '1.23'
 */
export function sanitizeDecimalInput(
  value: string,
  locale: string,
  maxFractionDigits?: number | null,
): string {
  const decimalSep = getDecimalSeparator(locale);
  const wrongSep = decimalSep === '.' ? ',' : '.';

  // Strip characters that can never be part of a numeric string, then
  // restrict minus to the leading position only
  let cleaned = value.replace(/[^\d.,-]/g, '').replace(/(?<=.)-/g, '');

  // Normalise separator: replace the one not used by this locale
  cleaned = cleaned.replace(wrongSep === '.' ? /\./g : /,/g, decimalSep);

  // Preserve only the first decimal separator and optionally truncate decimal digits
  const firstSep = cleaned.indexOf(decimalSep);
  if (firstSep !== -1) {
    if (maxFractionDigits === 0) {
      cleaned = cleaned.slice(0, firstSep);
    } else {
      let after = cleaned
        .slice(firstSep + 1)
        .replace(decimalSep === '.' ? /\./g : /,/g, '');

      if (maxFractionDigits != null) {
        after = after.slice(0, maxFractionDigits);
      }

      cleaned = cleaned.slice(0, firstSep + 1) + after;
    }
  }

  return cleaned;
}

export interface DecimalParserOptions {
  /**
   * When set, the parsed number is rounded to this many decimal places via `toFixed(n)`.
   * Useful when you want parse and round to happen in one step.
   * @example
   * decimalParser('de-DE', { toFixedFractionDigits: 2 })('1,999') // returns 2
   */
  toFixedFractionDigits?: number | null;
}

/**
 * Creates a parser function that converts locale-formatted number strings to numbers.
 *
 * @param locales - The locale(s) to use for parsing. If not provided, uses the browser's locale.
 * @param options - Optional rounding behaviour (see {@link DecimalParserOptions}).
 * @returns A parser function that converts locale-formatted strings to numbers.
 * @example
 * const parser = decimalParser('de-DE');
 * parser('1.000,5') // returns 1000.5
 *
 * const rounded = decimalParser('en-US', { toFixedFractionDigits: 2 });
 * rounded('1,000.999') // returns 1001  (rounded to 2 d.p. → 1001.00 → 1001)
 *
 * const browserParser = decimalParser(); // uses browser locale
 * browserParser('1,000.5') // returns 1000.5 (if browser is en-US)
 */
export function decimalParser(
  locales?: string | string[],
  options?: DecimalParserOptions,
): (value: string) => number {
  // Resolve to browser locale if not provided
  const resolvedLocales = locales ?? navigator.language;

  // Use the formatter to determine locale-specific separators
  const testNumber = 1000.1;
  const formatter = numberFormatter(resolvedLocales, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const formatted = formatter(testNumber);

  // Extract separators from formatted number "1,000.1" or "1.000,1"
  const decimalSeparator = formatted.charAt(formatted.length - 2);
  const groupingSeparator = formatted.charAt(1);

  return (value: string): number => {
    if (!value || typeof value !== 'string') {
      return 0;
    }

    let normalized = value.split(groupingSeparator).join('');

    // Replace locale-specific decimal separator with standard '.'
    if (decimalSeparator !== '.') {
      normalized = normalized.replace(decimalSeparator, '.');
    }

    const result = parseFloat(normalized) || 0;

    if (options?.toFixedFractionDigits != null) {
      return parseFloat(result.toFixed(options.toFixedFractionDigits));
    }

    return result;
  };
}
